import React, { useState } from 'react';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import { operatorId } from '../operatorGroups';
import { isExpenseAccount, OPERATOR_TYPE_SHORT_LABELS } from '../../../Utils/operatorType';
import UserService from '../../../Services/UserService';
import Permissions from '../../../Utils/Permissions';

/**
 * One row per COMPANY, not per record.
 *
 * A company that settles in two currencies is two Operator records, and two
 * rows made it look like two unrelated suppliers sitting next to each other
 * in the list. Here it is one row carrying both buckets as badges.
 *
 * The per-record screens behind the row — drivers, fleet, pricing, portal
 * users — are still per RECORD: each bucket has its own drivers and its own
 * pricing profile, and folding the rows together must not put either out of
 * reach. So when a company has more than one bucket the badges become the
 * chooser: the chosen bucket is what every action on the row opens. A
 * company with one bucket has nothing to choose, and renders exactly the
 * static badge and the same five actions as before.
 */
function CurrencyBadges({ group, selectedId, onSelect }) {
  if (!group.linked) {
    return group.currencies.map((currency) => (
      <span key={currency} className="badge bg-light text-dark border">{currency}</span>
    ));
  }

  return (
    <ButtonGroup size="sm" aria-label={`Settlement currency for ${group.name}`}>
      {group.members.map((member) => {
        const id = operatorId(member);
        const chosen = id === selectedId;

        return (
          <Button
            key={id}
            size="sm"
            variant={chosen ? 'primary' : 'outline-primary'}
            aria-pressed={chosen}
            onClick={() => onSelect(id)}
          >
            {member.settlement_currency}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}

/**
 * The actions a row offers, in the order the office reads them.
 *
 * Edit is the one that earns a button of its own. The rest live in the menu
 * beside it — six labelled buttons in six different colours made the action
 * column wider than the data it belonged to, and every new action made it
 * worse. A seventh now costs one more line in a menu instead of another
 * inch of table.
 */
const PRIMARY_ACTION = {
  key: 'edit', label: 'Edit', icon: 'fa-edit', variant: 'primary',
};

const MENU_ACTIONS = [
  { key: 'drivers', label: 'Drivers', icon: 'fa-user' },
  { key: 'fleet', label: 'Fleet', icon: 'fa-car' },
  { key: 'pricing', label: 'Pricing', icon: 'fa-calculator' },
  { key: 'portalUsers', label: 'Portal users', icon: 'fa-key' },
  { key: 'addCurrency', label: 'Add currency', icon: 'fa-coins' },
  // Behind ManageAi: the modal is where this operator's paperwork is
  // uploaded and deleted, and the whole of it is a managing action.
  // Filtered out of the menu rather than shown disabled — a menu entry
  // nobody can use is a question nobody can answer.
  {
    key: 'documents',
    label: 'Documents',
    icon: 'fa-folder-open',
    module: Permissions.MANAGE_AI,
  },
];

function OperatorGroupRow({ group, onAction }) {
  // The bucket the row's actions act on. The company's own record leads.
  const [selectedId, setSelectedId] = useState(group.key);
  const selected = group.members.find((member) => operatorId(member) === selectedId)
    || group.primary;

  const run = (key) => onAction(key, selected);

  const user = UserService.getInstance();
  const menuActions = MENU_ACTIONS.filter(
    (action) => !action.module || user.canAccessModule(action.module),
  );

  return (
    <tr>
      {/* The badges sit with the name, not in a column of their own. For a
          company with two buckets they are not decoration — they choose
          which record the row's actions open — so they had to move here
          rather than disappear with the Settlement Currency column. */}
      <td data-label="Name">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span>{group.name}</span>
          {/* Shown only for an expense account. A badge on all 130 rows
              saying "Operator" is noise on 120 of them; the ~10 that are
              cost buckets are the ones somebody needs to pick out — both to
              check the classification after setting it, and to understand
              why the record is missing from an assignment picker. */}
          {isExpenseAccount(group.primary) && (
            <span
              className="badge bg-secondary-subtle text-secondary-emphasis border"
              title="A cost bucket, not a supplier — never offered when a trip is assigned"
            >
              {OPERATOR_TYPE_SHORT_LABELS.expense}
            </span>
          )}
          <CurrencyBadges group={group} selectedId={selectedId} onSelect={setSelectedId} />
        </div>
      </td>
      <td data-label="E-mail address">{group.primary.email}</td>
      <td data-label="Phone number">{group.primary.phone_number}</td>
      <td data-label="Active">{group.primary.active ? 'Yes' : 'No'}</td>
      <td data-label="Daily Digest">
        {group.primary.daily_digest_enabled === 1 ? (
          <span className="badge bg-success">Enabled</span>
        ) : (
          <span className="badge bg-secondary">Disabled</span>
        )}
      </td>
      <td>
        {/* One button and a menu, at every width. Six labelled buttons in
            six colours made the action column wider than the data beside
            it, and every action added since made it worse. */}
        {/* The split button the bookings list uses (OrdersTable
            `renderRowActions`): the primary action and the menu toggle
            joined, rather than two buttons with a gap between them. */}
        <Dropdown as={ButtonGroup} size="sm" align="end">
          <Button
            variant={PRIMARY_ACTION.variant}
            onClick={() => run(PRIMARY_ACTION.key)}
          >
            <i className={`fa ${PRIMARY_ACTION.icon} me-1`} />
            {PRIMARY_ACTION.label}
          </Button>

          <Dropdown.Toggle
            split
            variant="outline-primary"
            id={`op-actions-${group.key}`}
            aria-label={`More actions for ${group.name}`}
          />

          <Dropdown.Menu>
            {menuActions.map((action) => (
              <Dropdown.Item key={action.key} onClick={() => run(action.key)}>
                <i className={`fa ${action.icon} me-2`} />
                {action.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  );
}

export default OperatorGroupRow;
